import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AirtimeMap, Source, Layer, FullscreenControl } from '../components/airtime_map'
import { TakeOffPayload, FlightPayload, MediaPayload, Exposition, countryFlag } from './types'
import { Button } from '../components/button'
import { ArrowLeftIcon, PencilIcon, CheckIcon, XMarkIcon, PhotoIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { RichTextEditor } from './flight_post'
import { FlightList } from './flight_list'
import { useAuthStatus } from './user_dropdown'

function parseCoords(s: string): [number, number] | null {
  const parts = s.split(',').map(p => parseFloat(p.trim()))
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null
  return [parts[1], parts[0]] // [lng, lat] for the map renderer
}

function InlineName({
  takeoffId,
  initial,
  onSaved,
}: {
  takeoffId: number
  initial: string
  onSaved: (name: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const save = async () => {
    const trimmed = value.trim()
    if (!trimmed) { setValue(initial); setEditing(false); return }
    setEditing(false)
    await fetch(`/v0/takeoffs/${takeoffId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed }),
    })
    onSaved(trimmed)
  }

  const cancel = () => { setValue(initial); setEditing(false) }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save()
    if (e.key === 'Escape') cancel()
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          className="text-2xl font-bold text-[#2D3142] dark:text-white bg-transparent border-b-2 border-[#EF8354] outline-none"
        />
        <button onClick={save} className="text-[#EF8354] hover:text-[#EF8354]/80">
          <CheckIcon className="h-5 w-5" />
        </button>
        <button onClick={cancel} className="text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    )
  }

  return (
    <button onClick={() => setEditing(true)} className="group flex items-center gap-2 text-left">
      <span className="text-2xl font-bold text-[#2D3142] dark:text-white">{value}</span>
      <PencilIcon className="h-4 w-4 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

function EditableTakeoffMap({
  takeoffId,
  coordinates,
  onSaved,
}: {
  takeoffId: number
  coordinates: string
  onSaved: (coords: string) => void
}) {
  const lngLat = useMemo(() => parseCoords(coordinates), [coordinates])
  const [pin, setPin] = useState<[number, number] | null>(lngLat)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // reset when coordinates prop changes (e.g. parent refresh)
  useEffect(() => {
    setPin(parseCoords(coordinates))
    setDirty(false)
  }, [coordinates])

  const handleClick = (e: { lngLat: { lng: number; lat: number } }) => {
    setPin([e.lngLat.lng, e.lngLat.lat])
    setDirty(true)
  }

  const saveCoords = async () => {
    if (!pin) return
    setSaving(true)
    const coords = `${pin[1].toFixed(6)},${pin[0].toFixed(6)}`
    await fetch(`/v0/takeoffs/${takeoffId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ coordinates: coords }),
    })
    setSaving(false)
    setDirty(false)
    onSaved(coords)
  }

  const cancelMove = () => {
    setPin(lngLat)
    setDirty(false)
  }

  const pointData = pin
    ? { type: 'Feature' as const, geometry: { type: 'Point' as const, coordinates: pin }, properties: {} }
    : null

  if (!lngLat) {
    return (
      <div className="h-full bg-gray-100 dark:bg-[#2D3142] rounded-lg flex items-center justify-center">
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">No location data</p>
      </div>
    )
  }

  return (
    <div className="relative h-full">
      <AirtimeMap
        style={{ width: '100%', height: '100%' }}
        initialViewState={{ longitude: lngLat[0], latitude: lngLat[1], zoom: 12 }}
        onClick={handleClick}
        cursor="crosshair"
      >
        {pointData && (
          <Source id="takeoff-point" type="geojson" data={pointData}>
            <Layer
              id="takeoff-circle"
              type="circle"
              paint={{
                'circle-radius': 10,
                'circle-color': dirty ? '#4F5D75' : '#EF8354',
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
              }}
            />
          </Source>
        )}
        <FullscreenControl position="top-right" />
      </AirtimeMap>

      {dirty && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-lg bg-white dark:bg-[#2D3142] shadow-lg px-3 py-2">
          <span className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
            {pin ? `${pin[1].toFixed(5)}, ${pin[0].toFixed(5)}` : ''}
          </span>
          <button
            onClick={saveCoords}
            disabled={saving}
            className="rounded bg-[#EF8354] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#EF8354]/80 disabled:opacity-40 transition-colors"
          >
            {saving ? 'Saving…' : 'Save location'}
          </button>
          <button
            onClick={cancelMove}
            className="text-xs text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

function ReadOnlyTakeoffMap({ coordinates }: { coordinates: string }) {
  const lngLat = useMemo(() => parseCoords(coordinates), [coordinates])

  if (!lngLat) {
    return (
      <div className="h-full bg-gray-100 dark:bg-[#2D3142] rounded-lg flex items-center justify-center">
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">No location data</p>
      </div>
    )
  }

  const pointData = {
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: lngLat },
    properties: {},
  }

  return (
    <AirtimeMap
      style={{ width: '100%', height: '100%' }}
      initialViewState={{ longitude: lngLat[0], latitude: lngLat[1], zoom: 12 }}
    >
      <Source id="takeoff-point" type="geojson" data={pointData}>
        <Layer
          id="takeoff-circle"
          type="circle"
          paint={{
            'circle-radius': 10,
            'circle-color': '#EF8354',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          }}
        />
      </Source>
      <FullscreenControl position="top-right" />
    </AirtimeMap>
  )
}

const ALL_EXPOSITIONS: Exposition[] = [
  'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW',
]

// Centre angle (clockwise from top / 12-o'clock) for each direction.
const EXPOSITION_ANGLE: Record<Exposition, number> = {
  N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315,
}

// Build an SVG arc-wedge path for a sector of `span` degrees centred on
// `centreDeg` (clockwise from 12-o'clock), with outer radius `r` starting
// from origin (0,0).
function wedgePath(centreDeg: number, span: number, r: number): string {
  const half = span / 2
  const startDeg = centreDeg - half
  const endDeg = centreDeg + half
  const toRad = (d: number) => ((d - 90) * Math.PI) / 180 // SVG: 0° = 3-o'clock
  const sx = r * Math.cos(toRad(startDeg))
  const sy = r * Math.sin(toRad(startDeg))
  const ex = r * Math.cos(toRad(endDeg))
  const ey = r * Math.sin(toRad(endDeg))
  const large = span > 180 ? 1 : 0
  return `M0,0 L${sx},${sy} A${r},${r} 0 ${large} 1 ${ex},${ey} Z`
}

// Label position: place text just outside the wedge along its centre axis.
function labelPos(centreDeg: number, r: number): { x: number; y: number } {
  const rad = ((centreDeg - 90) * Math.PI) / 180
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) }
}

function ExpositionPicker({
  takeoffId,
  initial,
  canEdit,
  onSaved,
}: {
  takeoffId: number
  initial: Exposition[]
  canEdit: boolean
  onSaved: (expositions: Exposition[]) => void
}) {
  const [selected, setSelected] = useState<Set<Exposition>>(new Set(initial))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setSelected(new Set(initial))
  }, [initial.join(',')])

  const toggle = async (exp: Exposition) => {
    if (!canEdit) return
    const next = new Set(selected)
    if (next.has(exp)) next.delete(exp)
    else next.add(exp)
    setSelected(next)

    const arr = ALL_EXPOSITIONS.filter(e => next.has(e))
    setSaving(true)
    await fetch(`/v0/takeoffs/${takeoffId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expositions: arr }),
    })
    setSaving(false)
    onSaved(arr)
  }

  const wedgeR = 60   // outer radius of the pie wedges
  const labelR = 76   // radius at which labels are drawn
  const svgSize = 200  // viewBox dimensions (centred on 0,0 → -100..100)

  return (
    <div className="inline-flex flex-col items-center shrink-0" title={saving ? 'Saving…' : 'Expositions'}>
      {/* Compact compass rose — 8 equal pie slices with labels */}
      <svg
        viewBox={`${-svgSize / 2} ${-svgSize / 2} ${svgSize} ${svgSize}`}
        className="block"
        style={{ width: 120, height: 120 }}
        role="group"
        aria-label="Exposition compass"
      >
        {/* Wedge slices */}
        {ALL_EXPOSITIONS.map(exp => {
          const active = selected.has(exp)
          const angle = EXPOSITION_ANGLE[exp]
          return (
            <path
              key={exp}
              d={wedgePath(angle, 45, wedgeR)}
              onClick={() => toggle(exp)}
              style={{ '--angle': `${angle}deg`, cursor: canEdit ? 'pointer' : 'default' } as React.CSSProperties}
              className={`
                transition-colors duration-150 stroke-white dark:stroke-[#2D3142]
                ${active
                  ? 'fill-[#EF8354]'
                  : 'fill-[#BFC0C0]/20 dark:fill-[#4F5D75]/30'}
                ${canEdit ? 'hover:fill-[#EF8354]/70' : ''}
              `}
              strokeWidth={1.5}
              aria-label={exp}
              role="button"
              tabIndex={canEdit ? 0 : undefined}
              onKeyDown={e => { if (canEdit && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(exp) } }}
            />
          )
        })}

        {/* Direction labels outside the circle */}
        {ALL_EXPOSITIONS.map(exp => {
          const { x, y } = labelPos(EXPOSITION_ANGLE[exp], labelR)
          const active = selected.has(exp)
          return (
            <text
              key={`lbl-${exp}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              className={`
                text-[11px] font-bold pointer-events-none select-none
                ${active
                  ? 'fill-[#EF8354]'
                  : 'fill-[#4F5D75] dark:fill-[#BFC0C0]'}
              `}
            >
              {exp}
            </text>
          )
        })}

        {/* Centre dot */}
        <circle r={2} className="fill-[#BFC0C0]/40 dark:fill-[#4F5D75]/40" />
      </svg>
    </div>
  )
}

function TakeoffPhotoSection({ takeoffId, canEdit }: { takeoffId: number; canEdit: boolean }) {
  const [photos, setPhotos] = useState<MediaPayload[]>([])
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetch(`/v0/takeoffs/${takeoffId}/photos`)
      .then(r => r.json())
      .then(d => setPhotos(d.photos ?? []))
      .catch(() => {})
  }, [takeoffId])

  const uploadFile = useCallback(async (file: File) => {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`/v0/takeoffs/${takeoffId}/photos`, { method: 'POST', body: form })
      if (!res.ok) return
      const data = await res.json()
      setPhotos(prev => [...prev, data])
    } finally {
      setUploading(false)
    }
  }, [takeoffId])

  const handleFiles = (files: FileList | null) => {
    if (!files) return
    Array.from(files).forEach(f => { if (f.type.startsWith('image/')) uploadFile(f) })
  }

  const deletePhoto = async (id: string) => {
    await fetch(`/v0/media/${id}`, { method: 'DELETE' })
    setPhotos(prev => prev.filter(p => p.id !== id))
    if (lightboxIndex !== null) {
      const newPhotos = photos.filter(p => p.id !== id)
      if (newPhotos.length === 0) setLightboxIndex(null)
      else setLightboxIndex(i => i !== null ? Math.min(i, newPhotos.length - 1) : null)
    }
  }

  const prev = () => setLightboxIndex(i => i !== null ? (i - 1 + photos.length) % photos.length : null)
  const next = () => setLightboxIndex(i => i !== null ? (i + 1) % photos.length : null)

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (lightboxIndex === null) return
    if (e.key === 'ArrowLeft') prev()
    if (e.key === 'ArrowRight') next()
    if (e.key === 'Escape') setLightboxIndex(null)
  }, [lightboxIndex, photos.length])

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])

  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354]">Photos</p>
        {canEdit && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] dark:hover:text-[#EF8354] border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 hover:border-[#EF8354] transition-colors disabled:opacity-40"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Add photo'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      {photos.length === 0 && !canEdit ? null : (
        <div
          onDragOver={canEdit ? (e => { e.preventDefault(); setDragOver(true) }) : undefined}
          onDragLeave={canEdit ? (() => setDragOver(false)) : undefined}
          onDrop={canEdit ? (e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }) : undefined}
          className={`transition-colors rounded-lg ${dragOver ? 'bg-[#EF8354]/10 ring-2 ring-[#EF8354]/40' : ''}`}
        >
          {photos.length === 0 ? (
            canEdit && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-lg border-2 border-dashed border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 text-[#4F5D75] dark:text-[#BFC0C0] hover:border-[#EF8354]/60 hover:text-[#EF8354] transition-colors"
              >
                <PhotoIcon className="h-8 w-8 opacity-40" />
                <span className="text-sm">Drop photos here or click to upload</span>
              </button>
            )
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map((photo, i) => (
                <button
                  key={photo.id}
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-[#BFC0C0]/10 dark:bg-[#4F5D75]/10 hover:ring-2 hover:ring-[#EF8354] transition-all"
                >
                  <img
                    src={`/v0/media/${photo.id}`}
                    alt={photo.filename}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>

          {canEdit && (
            <button
              onClick={e => { e.stopPropagation(); deletePhoto(currentPhoto.id) }}
              className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-md bg-black/50 px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-black/70 transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          )}

          {photos.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); prev() }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronLeftIcon className="h-10 w-10" />
            </button>
          )}

          <img
            src={`/v0/media/${currentPhoto.id}`}
            alt={currentPhoto.filename}
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />

          {photos.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); next() }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronRightIcon className="h-10 w-10" />
            </button>
          )}

          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/60">
            {(lightboxIndex ?? 0) + 1} / {photos.length}
          </p>
        </div>
      )}
    </div>
  )
}

export function TakeoffDetails() {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()
  const { userData } = useAuthStatus()
  const canEdit = userData.authenticated

  const [takeoff, setTakeoff] = useState<TakeOffPayload | null>(null)
  const [flights, setFlights] = useState<FlightPayload[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!name) return
    const decoded = decodeURIComponent(name)

    fetch('/v0/takeoffs')
      .then(r => r.json())
      .then((takeoffsData) => {
        const found = (takeoffsData.takeoffs as TakeOffPayload[]).find(t => t.name === decoded)
        setTakeoff(found ?? null)
        if (!found) {
          setIsLoading(false)
          return
        }
        fetch(`/v0/flights?takeoffs=${found.id}`)
          .then(r => r.json())
          .then(flightsData => setFlights(flightsData.flights as FlightPayload[]))
          .finally(() => setIsLoading(false))
      })
  }, [name])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1A1C2E]">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[#BFC0C0] rounded w-1/4" />
            <div className="h-96 bg-[#BFC0C0] rounded" />
          </div>
        </div>
      </div>
    )
  }

  const displayName = takeoff?.name ?? (name ? decodeURIComponent(name) : 'Unknown takeoff')

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1A1C2E]">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Button plain onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
            Back
          </Button>
        </div>

        <div className="mb-8 flex items-center gap-4">
          <div className="min-w-0 flex-1">
            {canEdit && takeoff ? (
              <InlineName
                takeoffId={takeoff.id}
                initial={takeoff.name}
                onSaved={newName => {
                  setTakeoff(prev => prev ? { ...prev, name: newName } : prev)
                  navigate(`/takeoff/${encodeURIComponent(newName)}`, { replace: true })
                }}
              />
            ) : (
              <h1 className="text-2xl font-bold text-[#2D3142] dark:text-white">{displayName}</h1>
            )}
            {takeoff?.coordinates && (
              <p className="mt-1 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
                {takeoff.country && <span className="mr-1">{countryFlag(takeoff.country)}</span>}
                {takeoff.coordinates} · {takeoff.altitude}m
              </p>
            )}
          </div>
          {takeoff && (
            <ExpositionPicker
              takeoffId={takeoff.id}
              initial={takeoff.expositions ?? []}
              canEdit={canEdit}
              onSaved={expositions => setTakeoff(prev => prev ? { ...prev, expositions } : prev)}
            />
          )}
        </div>

        {takeoff?.coordinates && (
          <div className="mb-8 h-80 bg-white dark:bg-[#2D3142] shadow-lg rounded-lg overflow-hidden">
            {canEdit ? (
              <EditableTakeoffMap
                takeoffId={takeoff.id}
                coordinates={takeoff.coordinates}
                onSaved={coords => setTakeoff(prev => prev ? { ...prev, coordinates: coords } : prev)}
              />
            ) : (
              <ReadOnlyTakeoffMap coordinates={takeoff.coordinates} />
            )}
          </div>
        )}

        {takeoff && (
          <div className="mb-8">
            <RichTextEditor
              initial={takeoff.description}
              placeholder="Add a description for this takeoff…"
              onSave={description =>
                fetch(`/v0/takeoffs/${takeoff.id}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ description }),
                }).then(() => {})
              }
            />
          </div>
        )}

        {takeoff && (
          <TakeoffPhotoSection takeoffId={takeoff.id} canEdit={canEdit} />
        )}

        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">
            Flights from this takeoff
          </p>
          {flights.length === 0 ? (
            <p className="text-sm text-center text-[#4F5D75] dark:text-[#BFC0C0]">
              No flights recorded from this takeoff.
            </p>
          ) : (
            <FlightList flights={flights} />
          )}
        </div>
      </div>
    </div>
  )
}
